import { useState, useEffect, useRef, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { Skeleton } from "@mui/material";

/**
 * LazyLoader Component
 * Dynamically loads content when it becomes visible on the screen.
 *
 * @param {Function} fetchData - Function to fetch data.
 * @param {Array|null} data - Data to check if it is already loaded.
 * @param {boolean} refresh - If true, triggers a local refresh when in view.
 * @param {boolean} infiniteScroll - Enables infinite scroll behavior.
 * @param {Function} loadMore - Function to load more data for infinite scroll.
 * @param {Function} loadingComponent - Optional custom loader component.
 * @param {number} threshold - Visibility threshold (default: 0.2).
 * @param {string} className - Custom CSS class for styling.
 * @param {React.ReactNode} children - Content to be rendered inside the component.
 */
const LazyLoader = ({
  fetchData,
  data,
  refresh = false,
  infiniteScroll = false,
  loadMore,
  loadingComponent = null,
  threshold = 0.2,
  className = "",
  children,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold,
  });

  const [localRefresh, setLocalRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const observerRef = useRef(null);

  // **Trigger localRefresh when refresh prop changes**
  useEffect(() => {
    if (!refresh) {
      setLocalRefresh(true);
    }
  }, [refresh]);

  // **Fetch data only when inView & localRefresh is true**
  const handleFetchData = useCallback(async () => {
    if (!fetchData || !inView || !localRefresh || !!loading) return;

    setLoading(true);
    setError(null);
    try {
      await fetchData();
      setLocalRefresh(false); // Reset local refresh after fetching
    } catch (err) {
      console.error(err);
      setLoading(false);
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [fetchData, inView, localRefresh]);

  // **Run fetch only when localRefresh is true**
  useEffect(() => {
    if (localRefresh || (inView && (!data || data.length === 0))) {
      handleFetchData();
    }
  }, [localRefresh, handleFetchData]);

  // **Handle Infinite Scroll**
  useEffect(() => {
    if (!infiniteScroll || !loadMore || !inView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [inView, infiniteScroll, loadMore]);

  return (
    <div ref={ref} className={`lazy-loader ${className}`}>
      {/* Show loading skeleton if data is not loaded */}
      {loading ? (
        loadingComponent ? (
          loadingComponent
        ) : (
          <div className="loading-placeholder">
            <Skeleton variant="rectangular" width="100%" height={200} />
          </div>
        )
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setLocalRefresh(true)}>Retry</button>
        </div>
      ) : (
        <div className="fade-in">{children}</div>
      )}

      {/* Infinite Scroll Trigger */}
      {infiniteScroll && (
        <div ref={observerRef} className="load-more-trigger"></div>
      )}
    </div>
  );
};

export default LazyLoader;

// Original S03E07
